import axios from "axios";
import { API_URL } from "../constants";

/**
 * Positions catalogue («Pareigos») — erp-backend api/services/PositionsService.js,
 * contract in docs/prd/positions-catalogue.md §5. A position is a per-company
 * job title (LT + EN) an employee holds 1..n of; each position links to 0..n
 * work-security roles («pareigybės»).
 *
 * Bare axios, like every other service here — the global interceptor already
 * attaches credentials.
 */

/** A pareigybė linked to a position, with the sphere it lives in. */
export interface PositionRoleRef {
  id: string;
  name: string;
  areaId: string | null;
  areaName: string | null;
}

export interface Position {
  id: string;
  nameLT: string;
  /** Optional feminine LT title (§8); documents print it for a female employee. */
  nameLTFemale: string | null;
  nameEN: string | null;
  isArchived: boolean;
  holdersCount: number;
  roles: PositionRoleRef[];
  /** Keys of the rights every holder of the position gets (see types/Permissions). */
  permissions: string[];
}

export interface PositionHolder {
  userId: string;
  name: string;
}

/** The short shape the per-user / per-role endpoints answer with. */
export interface PositionRef {
  id: string;
  nameLT: string;
  nameLTFemale?: string | null;
  nameEN?: string | null;
}

/**
 * Normalises one catalogue row. The contract fixes the fields; the guards only
 * keep a missing optional field (older payload, test stub) from crashing a
 * table cell.
 */
function normalizePosition(raw: any): Position {
  return {
    id: String(raw?.id ?? ""),
    nameLT: String(raw?.nameLT ?? ""),
    nameLTFemale: raw?.nameLTFemale || null,
    nameEN: raw?.nameEN ?? null,
    isArchived: !!raw?.isArchived,
    holdersCount: Number(raw?.holdersCount) || 0,
    roles: Array.isArray(raw?.roles)
      ? raw.roles.map((role: any) => ({
          id: String(role?.id ?? ""),
          name: String(role?.name ?? ""),
          areaId: role?.areaId ?? null,
          areaName: role?.areaName ?? null,
        }))
      : [],
    permissions: Array.isArray(raw?.permissions) ? raw.permissions.map(String) : [],
  };
}

/**
 * Create/update answer the saved row. The contract does not say whether it
 * comes bare or wrapped (`{ position }`), so both are accepted here — the one
 * place that has to know.
 */
function unwrapPosition(data: any): Position {
  return normalizePosition(data && data.position ? data.position : data);
}

/** List answers are arrays per contract; a `{ positions }` wrapper is tolerated. */
function unwrapList<T>(data: any, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && Array.isArray(data[key])) return data[key] as T[];
  return [];
}

/**
 * `onboarding.positionIds` / `contractamendment.newPositionIds` are stored as
 * a JSON array in a longtext column; depending on the model attribute type the
 * API hands them over parsed or as the raw string. Always returns an array of
 * non-empty id strings, in stored order.
 */
export function parsePositionIds(raw: unknown): string[] {
  let value: unknown = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      value = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .map((id) => (id == null ? "" : String(id)))
    .filter((id) => id.length > 0);
}

/** GET /:companyId/positions[?includeArchived=1] */
export async function fetchPositions(
  companyId: string,
  opts?: { includeArchived?: boolean }
): Promise<Position[]> {
  const qs = opts?.includeArchived ? "?includeArchived=1" : "";
  const res = await axios.get(`${API_URL}/${companyId}/positions${qs}`);
  return unwrapList<any>(res.data, "positions").map(normalizePosition);
}

/** POST /:companyId/positions — Master | Access onboarding. */
export async function createPosition(
  companyId: string,
  data: { nameLT: string; nameLTFemale?: string | null; nameEN?: string | null }
): Promise<Position> {
  const res = await axios.post(`${API_URL}/${companyId}/positions`, data);
  return unwrapPosition(res.data);
}

/** PATCH /:companyId/positions/:id — the backend recomputes holders' text mirrors. */
export async function updatePosition(
  companyId: string,
  positionId: string,
  data: { nameLT?: string; nameLTFemale?: string | null; nameEN?: string | null }
): Promise<Position> {
  const res = await axios.patch(`${API_URL}/${companyId}/positions/${positionId}`, data);
  return unwrapPosition(res.data);
}

export async function archivePosition(companyId: string, positionId: string): Promise<void> {
  await axios.post(`${API_URL}/${companyId}/positions/${positionId}/archive`);
}

export async function restorePosition(companyId: string, positionId: string): Promise<void> {
  await axios.post(`${API_URL}/${companyId}/positions/${positionId}/restore`);
}

/**
 * POST /:companyId/positions/:id/merge — folds `sourceId` into `targetId`:
 * holders move, pareigybės are united, the source is archived.
 * `sourceAsFemaleName` (§8): when the target has no feminine title yet, the
 * source's LT name becomes it (e.g. «Buhalterė» merged into «Buhalteris»).
 */
export async function mergePosition(
  companyId: string,
  sourceId: string,
  targetId: string,
  opts?: { sourceAsFemaleName?: boolean }
): Promise<{ genderSetCount: number }> {
  const res = await axios.post(`${API_URL}/${companyId}/positions/${sourceId}/merge`, {
    targetId,
    ...(opts?.sourceAsFemaleName ? { sourceAsFemaleName: true } : {}),
  });
  // Holders whose gender the merge set to female (source used as the feminine title).
  return { genderSetCount: Number(res.data?.genderSetCount) || 0 };
}

/**
 * PUT /:companyId/positions/:id/roles — full replace of the position's
 * pareigybės. The backend only touches roles in spheres the caller manages
 * and keeps the rest, so sending the read-only ones back is harmless.
 */
export async function setPositionRoles(
  companyId: string,
  positionId: string,
  roleIds: string[]
): Promise<void> {
  await axios.put(`${API_URL}/${companyId}/positions/${positionId}/roles`, { roleIds });
}

/**
 * PUT /:companyId/positions/:id/permissions — replaces the rights the position
 * gives; they apply at once to everyone who holds it. Master only.
 */
export async function setPositionPermissions(
  companyId: string,
  positionId: string,
  permissions: string[]
): Promise<string[]> {
  const res = await axios.put(
    `${API_URL}/${companyId}/positions/${positionId}/permissions`,
    { permissions }
  );
  return res.data.permissions;
}

export async function fetchPositionHolders(
  companyId: string,
  positionId: string
): Promise<PositionHolder[]> {
  const res = await axios.get(`${API_URL}/${companyId}/positions/${positionId}/holders`);
  return unwrapList<PositionHolder>(res.data, "holders");
}

/** GET /:companyId/users/:userId/positions — the employee's current positions, ordered. */
export async function fetchUserPositions(
  companyId: string,
  userId: string
): Promise<PositionRef[]> {
  const res = await axios.get(`${API_URL}/${companyId}/users/${userId}/positions`);
  return unwrapList<PositionRef>(res.data, "positions");
}

/** GET /:companyId/work-security/roles/:roleId/positions — positions that bring this pareigybė. */
export async function fetchRolePositions(
  companyId: string,
  roleId: string
): Promise<PositionRef[]> {
  const res = await axios.get(
    `${API_URL}/${companyId}/work-security/roles/${roleId}/positions`
  );
  return unwrapList<PositionRef>(res.data, "positions");
}
